import React, { useState } from 'react';
import SchoolIcon from '@mui/icons-material/School';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GppGoodIcon from '@mui/icons-material/GppGood';

const AVATAR_PALETTE = [
  'bg-emerald-100 text-emerald-800',
  'bg-amber-100 text-amber-800',
  'bg-sky-100 text-sky-800',
  'bg-violet-100 text-violet-800',
  'bg-rose-100 text-rose-800',
];

function SectionIconWrap({ children }) {
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-[#2EBD36]">
      {children}
    </span>
  );
}

function CollapsibleSection({ title, icon, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 py-3.5 pr-1 text-left"
      >
        <span className="flex min-w-0 flex-1 items-center gap-3">
          {icon}
          <span className="text-[15px] font-bold text-slate-900">{title}</span>
        </span>
        <span className="shrink-0 text-slate-400">
          {open ? (
            <ExpandLessIcon sx={{ fontSize: 22 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 22 }} />
          )}
        </span>
      </button>
      {open ? <div className="pr-1 pb-4 pl-13 text-sm">{children}</div> : null}
    </div>
  );
}

function StatusPill({ children, variant }) {
  const cls =
    variant === 'ok'
      ? 'bg-emerald-50 text-emerald-700'
      : variant === 'wait'
        ? 'bg-rose-50 text-rose-600'
        : variant === 'pending'
          ? 'bg-amber-50 text-amber-700'
          : 'bg-slate-100 text-slate-600';
  return (
    <span
      className={`inline-flex rounded-md px-2.5 py-1 text-[11px] font-bold ${cls}`}
    >
      {children}
    </span>
  );
}

function PersonnelMatrix({ personnel }) {
  if (!personnel) return null;
  const rows = [
    { key: 'male', label: '남', ...personnel.male },
    { key: 'female', label: '여', ...personnel.female },
  ];
  const t = personnel.total;

  const Cell = ({ children, className = '' }) => (
    <td className={`border border-slate-200 px-2 py-2.5 ${className}`}>
      {children}
    </td>
  );

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full border-collapse text-center text-[13px]">
        <thead>
          <tr className="bg-slate-50 text-xs font-semibold text-slate-500">
            <Cell className="w-[14%]">구분</Cell>
            <Cell>대상</Cell>
            <Cell>실시</Cell>
            <Cell>불참</Cell>
          </tr>
        </thead>
        <tbody className="text-slate-800">
          {rows.map((row) => (
            <tr key={row.key}>
              <Cell className="bg-slate-50/80 font-medium text-slate-600">
                {row.label}
              </Cell>
              <Cell className="font-semibold">{row.target}</Cell>
              <Cell className="font-bold text-[#2EBD36]">{row.conducted}</Cell>
              <Cell
                className={`font-bold ${row.absent > 0 ? 'text-red-500' : 'text-slate-600'}`}
              >
                {row.absent}
              </Cell>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-emerald-50/60 text-sm font-bold">
            <Cell className="text-slate-700">계</Cell>
            <Cell className="text-slate-900">{t.target}</Cell>
            <Cell className="text-[#2EBD36]">{t.conducted}</Cell>
            <Cell className={t.absent > 0 ? 'text-red-500' : 'text-slate-700'}>
              {t.absent}
            </Cell>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * 안전보건교육일지 상세 (화면단 NOT_BASIC)
 * @param {Object} props.detail - SearchLogicPeaEduLogDetail1 결과
 */
export default function M_PEA_EDU_LOG_DETAIL({
  detail,
  onReject,
  onApprove,
  approveDisabled = false,
  rejectDisabled = false,
}) {
  if (!detail) return null;

  const headerVariant = detail.statusHeaderVariant || 'pending';
  const ov = detail.overview || {};

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 overflow-y-auto pb-28">
        <div className="p-4">
          <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
            <div className="flex gap-3 border-b border-slate-100 p-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-slate-100 to-slate-200 text-xl font-bold text-slate-600">
                {detail.authorInitial}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-base font-bold text-slate-900">
                  {detail.authorLine}
                </p>
                <p className="mt-0.5 text-sm text-slate-500">
                  {detail.authorDept}
                </p>
                <p className="mt-1 text-xs text-slate-400">{detail.eduDate}</p>
              </div>
              <StatusPill variant={headerVariant}>
                {detail.statusLabel}
              </StatusPill>
            </div>
            <div className="p-4">
              <p className="mb-2 text-xs font-medium text-slate-400">
                {detail.categorySectionTitle || '교육의 구분'}
              </p>
              <div className="rounded-xl border-2 border-[#2EBD36]/35 bg-emerald-50/40 p-3.5 text-sm leading-relaxed font-medium text-slate-800">
                {detail.categoryLabel}
              </div>
              <p className="mt-4 text-center text-sm text-slate-600">
                총 교육 인원{' '}
                <span className="text-base font-bold text-slate-900">
                  {detail.totalParticipants}명
                </span>
              </p>
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100 bg-white px-3 shadow-sm">
            <CollapsibleSection
              title="교육 내용 및 정보"
              defaultOpen
              icon={
                <SectionIconWrap>
                  <SchoolIcon sx={{ fontSize: 20 }} />
                </SectionIconWrap>
              }
            >
              <div className="space-y-4">
                <div>
                  <p className="mb-1 text-xs font-medium text-slate-400">
                    교육명
                  </p>
                  <p className="text-[15px] font-semibold text-slate-900">
                    {detail.eduTitle}
                  </p>
                </div>
                <div>
                  <p className="mb-2 text-xs font-medium text-slate-400">
                    교육 내용
                  </p>
                  <ul className="space-y-2">
                    {detail.contentBullets.map((line, i) => (
                      <li
                        key={i}
                        className="flex gap-2.5 text-[14px] leading-relaxed text-slate-800"
                      >
                        <span
                          className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#2EBD36]"
                          aria-hidden
                        />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="교육 실시 개요"
              defaultOpen
              icon={
                <SectionIconWrap>
                  <CalendarMonthIcon sx={{ fontSize: 20 }} />
                </SectionIconWrap>
              }
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-400">
                      교육일
                    </p>
                    <p className="text-[15px] font-bold text-slate-900">
                      {ov.eduDate || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-400">
                      교육시간
                    </p>
                    <p className="text-[15px] font-bold text-slate-900">
                      {ov.eduTime || '-'}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium text-slate-400">
                    교육장소
                  </p>
                  <p className="text-[15px] font-bold text-slate-900">
                    {ov.eduPlace || '-'}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium text-slate-400">
                    교육담당자
                  </p>
                  <p className="text-[15px] font-bold text-slate-900">
                    {ov.eduManager || '-'}
                  </p>
                </div>
                {ov.instructorLine ? (
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-400">
                      강사
                    </p>
                    <p className="text-[15px] font-bold text-slate-900">
                      {ov.instructorLine}
                    </p>
                  </div>
                ) : null}
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="교육 대상 및 인원"
              defaultOpen
              icon={
                <SectionIconWrap>
                  <GroupsIcon sx={{ fontSize: 20 }} />
                </SectionIconWrap>
              }
            >
              <div className="space-y-4">
                <PersonnelMatrix personnel={detail.personnelTable} />
                <div>
                  <p className="mb-2 text-xs font-semibold text-slate-500">
                    미실시 사유
                  </p>
                  <p className="rounded-xl bg-slate-50 p-3.5 text-sm leading-relaxed text-slate-700">
                    {detail.absenceReasonText}
                  </p>
                </div>
              </div>
            </CollapsibleSection>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
            <div className="flex items-center border-b border-slate-100 px-4 py-3.5">
              <span className="flex items-center gap-2 text-[15px] font-bold text-slate-900">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-[#2EBD36]">
                  <FactCheckIcon sx={{ fontSize: 18 }} />
                </span>
                참석자 명단
              </span>
            </div>
            <div className="space-y-2 p-3">
              {detail.attendees.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-500">
                  등록된 참석자가 없습니다.
                </p>
              ) : (
                detail.attendees.map((a, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 rounded-2xl bg-slate-50 px-3 py-3"
                  >
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ${AVATAR_PALETTE[idx % AVATAR_PALETTE.length]}`}
                    >
                      {a.initial}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-slate-900">{a.name}</p>
                      <p className="text-xs text-slate-500">{a.dept}</p>
                    </div>
                    <StatusPill variant={a.signed ? 'ok' : 'wait'}>
                      {a.signed ? '서명완료' : '서명대기'}
                    </StatusPill>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100 bg-white px-3 shadow-sm">
            <CollapsibleSection
              title="교육 현장 사진"
              defaultOpen={false}
              icon={
                <SectionIconWrap>
                  <PhotoCameraOutlinedIcon sx={{ fontSize: 20 }} />
                </SectionIconWrap>
              }
            >
              {detail.images.length === 0 ? (
                <p className="text-sm text-slate-500">
                  등록된 사진이 없습니다.
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  {detail.images.map((img, i) => (
                    <figure
                      key={
                        img.filePath ||
                        img.saveName ||
                        img.src ||
                        `edu-img-${i}`
                      }
                      className="overflow-hidden rounded-xl border border-slate-100"
                    >
                      <img
                        src={img.src}
                        alt={img.alt || '교육 현장 사진'}
                        loading="lazy"
                        className="max-h-64 w-full bg-slate-50 object-contain"
                      />
                      {img.caption || img.alt ? (
                        <figcaption className="px-2 py-1.5 text-xs text-slate-500">
                          {img.caption || img.alt}
                        </figcaption>
                      ) : null}
                    </figure>
                  ))}
                </div>
              )}
            </CollapsibleSection>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
            <p className="mb-2 text-[15px] font-bold text-slate-900">
              특이사항
            </p>
            <p className="rounded-xl bg-slate-50 p-3.5 text-sm leading-relaxed text-slate-700">
              {detail.specialNotes}
            </p>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
            <p className="mb-4 text-[15px] font-bold text-slate-900">
              결재 현황
            </p>
            <p className="mb-2 text-xs font-bold text-slate-500">중간 결재자</p>
            {detail.approvers.intermediate.length === 0 ? (
              <p className="mb-5 text-sm text-slate-500">
                중간 결재자가 없습니다.
              </p>
            ) : (
              <div className="mb-5 space-y-3">
                {detail.approvers.intermediate.map((ap, i) => (
                  <div
                    key={i}
                    className="flex gap-3 rounded-xl bg-slate-50 px-3 py-3"
                  >
                    <div
                      className={`shrink-0 pt-0.5 ${(ap.statusVariant || (ap.done ? 'ok' : 'pending')) === 'ok' ? 'text-[#2EBD36]' : 'text-slate-300'}`}
                    >
                      {(ap.statusVariant || (ap.done ? 'ok' : 'pending')) ===
                      'ok' ? (
                        <CheckCircleIcon sx={{ fontSize: 22 }} />
                      ) : (
                        <RadioButtonUncheckedIcon
                          sx={{ fontSize: 22 }}
                          className="text-slate-300"
                        />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-slate-900">
                        {ap.name}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-400">
                        {ap.done && ap.time ? ap.time : ap.subtitle}
                      </p>
                    </div>
                    <div className="shrink-0 self-start">
                      <StatusPill
                        variant={
                          ap.statusVariant || (ap.done ? 'ok' : 'pending')
                        }
                      >
                        {ap.statusLabel ||
                          (ap.done ? '결재완료' : '결재요청')}
                      </StatusPill>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="mb-2 text-xs font-bold text-slate-500">최종 결재자</p>
            <div className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-bold text-slate-500">
                {String(detail.approvers.final.name).charAt(0) || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-900">
                  {detail.approvers.final.name}
                </p>
              </div>
              <StatusPill
                variant={
                  detail.approvers.final.statusVariant ??
                  (detail.approvers.final.done ? 'ok' : 'pending')
                }
              >
                {detail.approvers.final.statusLabel ??
                  (detail.approvers.final.done
                    ? '결재완료'
                    : '결재대기')}
              </StatusPill>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed right-0 bottom-0 left-0 z-10 flex gap-3 border-t border-slate-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
        <button
          type="button"
          onClick={onReject}
          disabled={rejectDisabled}
          className="flex-1 rounded-2xl bg-slate-200 py-3.5 text-sm font-bold text-slate-800 disabled:pointer-events-none disabled:opacity-45"
        >
          반려
        </button>
        <button
          type="button"
          onClick={onApprove}
          disabled={approveDisabled}
          className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-[#2EBD36] py-3.5 text-sm font-bold text-white shadow-md disabled:pointer-events-none disabled:opacity-45"
        >
          <GppGoodIcon sx={{ fontSize: 22 }} />
          승인
        </button>
      </div>
    </div>
  );
}
